import React, { useState, useEffect, useContext, useCallback } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Avatar, 
  Chip, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  Menu,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { CONTEXTS, CONTROLLERS, SERVICES } from 'figshelf-modules';

const adminLevels = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'system_admin', label: 'System Admin' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'content_manager', label: 'Content Manager' },
  { value: 'support_admin', label: 'Support Admin' },
  { value: 'finance_admin', label: 'Finance Admin' },
  { value: 'read_only_admin', label: 'Read Only Admin' }
];

const AdminManagement = () => {
  const api = useContext(CONTEXTS.APIContext);
  const auth = useContext(CONTEXTS.AuthContext);
  
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuAdmin, setMenuAdmin] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    password: '',
    adminLevel: 'system_admin',
    displayName: '',
    bio: ''
  });
  
  const [editForm, setEditForm] = useState({
    adminLevel: ''
  });

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const adminController = new CONTROLLERS.AdminController(api);

      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'admins',
        params: { role: 'admin', skip: 0, take: 100 },
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 5 * 60 * 1000 });
      if (cached?.data && Array.isArray(cached.data)) {
        setAdmins(cached.data);
      }

      const { data } = await adminController.getUsers({ role: 'admin', skip: 0, take: 100 });
      setAdmins(data);
      await SERVICES.cacheAdminList(cacheKey, { data }, { scope: 'admins' });
    } catch (error) {
      console.error('Error fetching admins:', error);
      setSnackbar({ open: true, message: 'Failed to fetch admins', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [api, auth?.user?.id]);

  const handleCreateAdmin = async () => {
    try {
      const adminController = new CONTROLLERS.AdminController(api);
      await adminController.createAdmin(createForm);
      setSnackbar({ open: true, message: 'Admin created successfully', severity: 'success' });
      setOpenCreateDialog(false);
      setCreateForm({
        username: '',
        email: '',
        password: '',
        adminLevel: 'system_admin',
        displayName: '',
        bio: ''
      });
      fetchAdmins();
    } catch (error) {
      console.error('Error creating admin:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to create admin', severity: 'error' });
    }
  };

  const handleUpdateAdminLevel = async () => {
    try {
      const adminController = new CONTROLLERS.AdminController(api);
      await adminController.updateAdminLevel(selectedAdmin.id, editForm);
      setSnackbar({ open: true, message: 'Admin level updated successfully', severity: 'success' });
      setOpenEditDialog(false);
      fetchAdmins();
    } catch (error) {
      console.error('Error updating admin:', error);
      setSnackbar({ open: true, message: 'Failed to update admin level', severity: 'error' });
    }
  };

  const handleMenuOpen = (event, admin) => {
    setAnchorEl(event.currentTarget);
    setMenuAdmin(admin);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuAdmin(null);
  };

  const handleEditClick = (admin) => {
    setSelectedAdmin(admin);
    setEditForm({ adminLevel: admin?.adminLevel || '' });
    setOpenEditDialog(true);
    handleMenuClose();
  };

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const getAdminLevelColor = (level) => {
    const colors = {
      super_admin: 'error',
      system_admin: 'warning',
      content_manager: 'info',
      support_admin: 'success',
      finance_admin: 'primary',
      read_only_admin: 'default'
    };
    return colors[level] || 'default';
  };

  return (
    <MainLayout>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600} mb={1}>Admin Management</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Manage system administrators and their permissions
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateDialog(true)}
            sx={{ borderRadius: 2 }}
          >
            Create Admin
          </Button>
        </Box>

        {/* Stats Card */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'error.light', mr: 2 }}>
                    <AdminPanelSettingsIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{admins.length}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Admins</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Admins Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Admin</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Admin Level</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && admins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : admins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">No admins found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                admins.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2 }} src={admin.getAvatar ? admin.getAvatar() : null}>
                          {(admin.getDisplayName ? admin.getDisplayName() : admin.username)?.[0]?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {admin.getDisplayName ? admin.getDisplayName() : admin.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{admin.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={adminLevels.find(l => l.value === admin.adminLevel)?.label || admin.adminLevel || '—'}
                        color={getAdminLevelColor(admin.adminLevel)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {admin.totalActionsPerformed || 0} actions
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, admin)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleEditClick(menuAdmin)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit Admin Level
          </MenuItem>
        </Menu>

        {/* Create Admin Dialog */}
        <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Admin</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Username"
                value={createForm.username}
                onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                fullWidth
                required
              />
              <FormControl fullWidth required>
                <InputLabel>Admin Level</InputLabel>
                <Select
                  value={createForm.adminLevel}
                  onChange={(e) => setCreateForm({ ...createForm, adminLevel: e.target.value })}
                  label="Admin Level"
                >
                  {adminLevels.map((level) => (
                    <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Display Name"
                value={createForm.displayName}
                onChange={(e) => setCreateForm({ ...createForm, displayName: e.target.value })}
                fullWidth
              />
              <TextField
                label="Bio"
                value={createForm.bio}
                onChange={(e) => setCreateForm({ ...createForm, bio: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAdmin} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Admin Dialog */}
        <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Update Admin Level</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Admin Level</InputLabel>
                <Select
                  value={editForm.adminLevel}
                  onChange={(e) => setEditForm({ adminLevel: e.target.value })}
                  label="Admin Level"
                >
                  {adminLevels.map((level) => (
                    <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateAdminLevel} variant="contained">Update</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  );
};

export default AdminManagement;
